import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { database } from "../firebaseConfig";
import { ref, update, onValue, push, set, remove } from "firebase/database";
import "./AITagsSettings.css";

// Modal Komponente die außerhalb des Container-DOM gerendert wird
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div className="aiTagsModernModal">
      <div className="aiTagsModernModalOverlay" onClick={onClose}></div>
      <div className="aiTagsModernModalContent">
        {children}
      </div>
    </div>,
    document.body
  );
};

const AITagsSettings = ({ userId }) => {
  const [tags, setTags] = useState([]);
  const [showAddTag, setShowAddTag] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [newTag, setNewTag] = useState({
    name: '',
    case: '',
    tag: '',
    description: '',
    color: '#1e40af',
    icon: '',
    overwriteOtherTags: true,
    selectedTopic: ''
  });
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagsMessage, setTagsMessage] = useState('');
  const [userTopics, setUserTopics] = useState({});
  const [infoCollapsed, setInfoCollapsed] = useState(true);

  // Add predefined colors array at the top of the component
  const predefinedColors = [
    '#1e40af', // Dark Blue
    '#1d4ed8', // Royal Blue
    '#7c3aed', // Purple
    '#6d28d9', // Deep Purple
    '#be185d', // Pink
    '#9f1239', // Deep Pink
    '#facc15', // Neon Yellow
    '#fbbf24', // Bright Yellow
    '#f59e0b', // Bright Orange
    '#eab308', // Vibrant Yellow
    '#dc2626', // Red
    '#ea580c', // Orange
    '#84cc16', // Lime Green
    '#22c55e', // Neon Green
    '#4f46e5', // Indigo
    '#8b5cf6', // Bright Purple
    '#ec4899', // Hot Pink
    '#f43f5e', // Neon Pink
  ];

  // Verfügbare Icons mit Beschreibungen für welche Tags sie passen
  const availableIcons = [
    { name: 'calendar', path: '/img/tag_calendar.svg', suggestedFor: ['meeting', 'schedule', 'appointment', 'follow-up'] },
    { name: 'close', path: '/img/tag_close.svg', suggestedFor: ['rejected', 'closed', 'not-interested', 'cancelled'] },
    { name: 'coin', path: '/img/tag_coin.svg', suggestedFor: ['payment', 'invoice', 'pricing', 'budget', 'costs'] },
    { name: 'eye', path: '/img/tag_eye.svg', suggestedFor: ['review', 'looking', 'interested', 'watching'] },
    { name: 'rotate', path: '/img/tag_rotate.svg', suggestedFor: ['pending', 'processing', 'in-progress', 'waiting'] },
    { name: 'time', path: '/img/tag_time.svg', suggestedFor: ['urgent', 'deadline', 'delayed', 'waiting'] },
    { name: 'zoom', path: '/img/tag_zoom.svg', suggestedFor: ['call', 'meeting', 'video', 'conference'] }
  ];

  // Funktion zum Vorschlagen eines passenden Icons basierend auf Tag-Name oder Case
  const suggestIcon = (tagName, tagCase) => {
    const searchText = (tagName + ' ' + tagCase).toLowerCase();
    
    return availableIcons.find(icon => 
      icon.suggestedFor.some(suggestion => 
        searchText.includes(suggestion)
      )
    )?.path || '';
  };

  // Lade Tags aus der Datenbank
  useEffect(() => {
    if (!userId) return;
    
    const tagsRef = ref(database, `/World/${userId}/settings/aiTags`);
    const unsubscribe = onValue(tagsRef, (snapshot) => {
      if (snapshot.exists()) {
        const tagsData = [];
        snapshot.forEach((childSnapshot) => {
          tagsData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        setTags(tagsData);
      } else {
        setTags([]);
      }
      setTagsLoading(false);
    });
    
    return () => unsubscribe();
  }, [userId]);

  // Lade die verfügbaren Topics
  useEffect(() => {
    if (!userId) return;
    
    // Lade sowohl selectedTopics als auch customTopics
    const selectedTopicsRef = ref(database, `/World/${userId}/settings/selectedTopics`);
    const customTopicsRef = ref(database, `/World/${userId}/settings/customTopics`);
    
    const unsubscribeSelected = onValue(selectedTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserTopics(prev => ({
          ...prev,
          ...snapshot.val()
        }));
      }
    });
    
    const unsubscribeCustom = onValue(customTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserTopics(prev => ({
          ...prev,
          ...snapshot.val()
        }));
      }
    });
    
    return () => {
      unsubscribeSelected();
      unsubscribeCustom();
    };
  }, [userId]);

  // Funktion zum Hinzufügen eines neuen Tags
  const handleAddTag = async () => {
    if (!newTag.name.trim() || !newTag.case.trim() || !newTag.tag.trim()) {
      setTagsMessage('Please fill in all required fields.');
      return;
    }
    
    try {
      setTagsMessage('Saving tag...');
      
      const tagsRef = ref(database, `/World/${userId}/settings/aiTags`);
      const newTagRef = push(tagsRef);
      
      const tagData = {
        name: newTag.name.trim(),
        case: newTag.case.trim(),
        tag: newTag.tag.trim(),
        description: newTag.description.trim(),
        color: newTag.color,
        icon: newTag.icon,
        overwriteOtherTags: true,
        selectedTopic: newTag.selectedTopic,
        createdAt: new Date().toISOString()
      };
      
      await set(newTagRef, tagData);
      
      setTagsMessage('Tag successfully added.');
      setShowAddTag(false);
      setNewTag({ 
        name: '', 
        case: '', 
        tag: '', 
        description: '', 
        color: '#1e40af', 
        icon: '',
        overwriteOtherTags: true,
        selectedTopic: ''
      });
      setTimeout(() => setTagsMessage(''), 3000);
    } catch (error) {
      console.error('Error adding tag:', error);
      setTagsMessage(`Error: ${error.message}`);
    }
  };

  // Funktion zum Aktualisieren eines Tags
  const handleUpdateTag = async () => {
    if (!editingTag || !editingTag.name.trim() || !editingTag.case.trim() || !editingTag.tag.trim()) {
      setTagsMessage('Please fill in all required fields.');
      return;
    }
    
    try {
      setTagsMessage('Updating tag...');
      
      const tagRef = ref(database, `/World/${userId}/settings/aiTags/${editingTag.id}`);
      
      const updateData = {
        name: editingTag.name.trim(),
        case: editingTag.case.trim(),
        tag: editingTag.tag.trim(),
        description: editingTag.description.trim(),
        color: editingTag.color,
        icon: editingTag.icon,
        overwriteOtherTags: true,
        selectedTopic: editingTag.selectedTopic,
        updatedAt: new Date().toISOString()
      };
      
      console.log('Updating tag with data:', updateData);
      await update(tagRef, updateData);
      
      setTagsMessage('Tag successfully updated.');
      setEditingTag(null);
      setTimeout(() => setTagsMessage(''), 3000);
    } catch (error) {
      console.error('Error updating tag:', error);
      setTagsMessage(`Error: ${error.message}`);
    }
  };

  // Funktion zum Löschen eines Tags
  const handleDeleteTag = async (tag) => {
    if (!tag || !window.confirm(`Are you sure you want to delete the tag "${tag.name}"?`)) {
      return;
    }
    
    try {
      setTagsMessage('Deleting tag...');
      
      const tagRef = ref(database, `/World/${userId}/settings/aiTags/${tag.id}`);
      await remove(tagRef);
      
      setTagsMessage('Tag successfully deleted.');
      setTimeout(() => setTagsMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting tag:', error);
      setTagsMessage(`Error: ${error.message}`);
    }
  };

  // Funktion zum Bearbeiten eines Tags
  const handleEditTag = (tag) => {
    setEditingTag({
      ...tag,
      overwriteOtherTags: tag.overwriteOtherTags || false,
      selectedTopic: tag.selectedTopic || ''
    });
    setShowAddTag(false);
  };

  // Funktion zum Abbrechen der Bearbeitung
  const handleCancelEdit = () => {
    setEditingTag(null);
    setTagsMessage('');
  };

  // Funktion zum Abbrechen des Hinzufügens
  const handleCancelAdd = () => {
    setShowAddTag(false);
    setNewTag({ name: '', case: '', tag: '', description: '', color: '#1e40af', icon: '', overwriteOtherTags: true, selectedTopic: '' });
    setTagsMessage('');
  };

  // Funktion zum Öffnen des Add-Tag-Modals durch Container-Klick
  const handleContainerClick = (e) => {
    // Prüfe ob auf einem Tag-Item geklickt wurde
    const tagItem = e.target.closest('.tagItem');
    if (tagItem) {
      // Finde das entsprechende Tag anhand der tag-id
      const tagId = tagItem.getAttribute('data-tag-id');
      const clickedTag = tags.find(tag => tag.id === tagId);
      
      if (clickedTag && !showAddTag && !editingTag) {
        handleEditTag(clickedTag);
        return;
      }
    }
    
    // Nur Add-Modal öffnen wenn wir nicht bereits im Add- oder Edit-Modus sind
    // und wenn der Klick nicht auf einem Button oder Input-Element war
    // und nicht auf einem Tag-Item
    if (!showAddTag && !editingTag && !e.target.closest('button') && !e.target.closest('input') && !e.target.closest('textarea') && !e.target.closest('select') && !tagItem) {
      setShowAddTag(true);
    }
  };

  if (!userId) {
    return <p className="errorMessage">Please login to manage AI tags.</p>;
  }

  return (
    <>
      {/* Info Container über AI Tags */}
      <div className={`aiTagsInfoContainer ${infoCollapsed ? 'collapsed' : ''}`}>
        <div className="aiTagsInfoHeader" onClick={() => setInfoCollapsed(!infoCollapsed)}>
          <div className="aiTagsInfoIcon">
            <img src="/img/info.svg" alt="Info" style={{ width: '34px', height: '34px' }} />
          </div>
          <h3>What are AI Tags?</h3>
          <button className="aiTagsInfoToggle">
            <img 
              src="/img/down.svg" 
              alt="Toggle" 
              className={`toggleIcon ${infoCollapsed ? 'collapsed' : ''}`}
              style={{ width: '16px', height: '16px' }}
            />
          </button>
        </div>
        {!infoCollapsed && (
          <div className="aiTagsInfoContent">
            <p>
              AI Tags automatically analyze and categorize your incoming emails based on content, context, and patterns. 
              Create custom rules that help organize your inbox intelligently without manual sorting.
            </p>
            <div className="aiTagsInfoFeatures">
              <div className="aiTagsInfoFeature">
                <span className="featureIcon">🎯</span>
                <span>Auto-categorize emails by content</span>
              </div>
              <div className="aiTagsInfoFeature">
                <span className="featureIcon">🔗</span>
                <span>Link topics for smart replies</span>
              </div>
              <div className="aiTagsInfoFeature">
                <span className="featureIcon">⚡</span>
                <span>Boost productivity with automation</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="aiTagsSettingsContainer" onClick={handleContainerClick}>
        <div className="aiTagsHeader">
          <h3>
            <div className="aiTagsHeader-iconContainer">
              <img src="/img/price-tag.svg" alt="AI" className="aiTagsHeader-icon" style={{ width: '24px', height: '24px' }} />
            </div>
            AI Tags Management
          </h3>
          {!showAddTag && !editingTag && (
            <button 
              className="addTagButton"
              onClick={() => {
                setEditingTag(null);
                setShowAddTag(true);
              }}
            >
              + Add Tag
            </button>
          )}
        </div>
        
        {tagsLoading ? (
          <div className="tagsLoading">Loading tags...</div>
        ) : (
          <>
            {editingTag && (
              <Modal isOpen={!!editingTag} onClose={handleCancelEdit}>
                <div className="tagForm editForm">
                  <div className="tagFormHeader">
                    <div className="formIcon">✏️</div>
                    <h4>Edit Tag</h4>
                    <button className="aiTagsModernModalCloseButton" onClick={handleCancelEdit}>×</button>
                  </div>
                  
                  <div className="aiTagsModernModalFormBody">
                    <div className="settingField">
                      <label>Name:</label>
                      <input
                        type="text"
                        className="aiTagsModernModalInput"
                        value={editingTag?.name || ''}
                        onChange={(e) => setEditingTag(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter tag name"
                      />
                    </div>
                    
                    <div className="settingField">
                      <label>Case Description:</label>
                      <textarea
                        className="aiTagsModernModalTextarea"
                        value={editingTag?.case || ''}
                        onChange={(e) => setEditingTag(prev => ({ ...prev, case: e.target.value }))}
                        placeholder="Describe when this tag should be applied"
                        rows="3"
                      />
                      <small className="fieldHint">
                        Describe the conditions under which this tag should be applied to an email.
                      </small>
                    </div>
                    
                    <div className="settingField">
                      <label>Tag:</label>
                      <input
                        type="text"
                        className="aiTagsModernModalInput"
                        value={editingTag?.tag || ''}
                        onChange={(e) => setEditingTag(prev => ({ ...prev, tag: e.target.value }))}
                        placeholder="Enter tag value (e.g., 'urgent', 'follow-up')"
                      />
                      <small className="fieldHint">
                        This is the actual tag that will be applied to matching emails.
                      </small>
                    </div>
                    
                    <div className="settingField">
                      <label>Description:</label>
                      <textarea
                        className="aiTagsModernModalTextarea"
                        value={editingTag?.description || ''}
                        onChange={(e) => setEditingTag(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description of this tag's purpose"
                        rows="2"
                      />
                    </div>
                    
                    <div className="settingField">
                      <label>Color:</label>
                      <div className="aiTags-colorSelection">
                        {predefinedColors.map((color) => (
                          <button
                            key={color}
                            className={`aiTags-colorOption ${(editingTag?.color || '#1e40af') === color ? 'aiTags-colorOption--selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setEditingTag(prev => ({ ...prev, color: color }))}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                    
                    <div className="settingField">
                      <label>Icon:</label>
                      <div className="aiTags-iconSelection">
                        <button
                          className={`aiTags-iconOption ${!(editingTag?.icon) ? 'aiTags-iconOption--selected' : ''}`}
                          onClick={() => setEditingTag(prev => ({ ...prev, icon: '' }))}
                        >
                          None
                        </button>
                        {availableIcons.map((icon) => (
                          <button
                            key={icon.name}
                            className={`aiTags-iconOption ${(editingTag?.icon) === icon.path ? 'aiTags-iconOption--selected' : ''}`}
                            onClick={() => setEditingTag(prev => ({ ...prev, icon: icon.path }))}
                          >
                            <img src={icon.path} alt={icon.name} className="aiTags-iconPreview" />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="settingField">
                      <label>Topic for Reply:</label>
                      <select
                        className="aiTagsModernModalSelect"
                        value={editingTag?.selectedTopic || ''}
                        onChange={(e) => setEditingTag(prev => ({ ...prev, selectedTopic: e.target.value }))}
                      >
                        <option value="">No automatic reply topic</option>
                        {Object.entries(userTopics)
                          .filter(([_, topic]) => topic.type === 'custom_reply')
                          .map(([id, topic]) => (
                            <option key={id} value={topic.topic}>
                              {topic.topic}
                            </option>
                          ))
                        }
                      </select>
                      <small className="fieldHint">
                        When this tag is applied, this topic will be automatically selected for replies.
                      </small>
                    </div>
                  </div>
                  
                  <div className="aiTagsModernModalFormActions">
                    <div className="tagFormActions">
                      <button 
                        className="cancelTagButton"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                      <button 
                        className="saveTagButton"
                        onClick={handleUpdateTag}
                      >
                        Update Tag
                      </button>
                    </div>
                  </div>
                </div>
              </Modal>
            )}

            <div className="tagsContainer">
              {tags.length === 0 && !showAddTag ? (
                <div className="noTags">
                  <div className="noTags-content">
                    <div className="noTags-icon">🏷️</div>
                    <h4>Create Your First AI Tag</h4>
                    <p>AI Tags automatically categorize incoming emails based on their content and context.</p>
                    
                    <div className="tagBenefits">
                      <div className="benefit">
                        <div className="benefitIcon">🎯</div>
                        <div className="benefitText">
                          <strong>Smart Categorization</strong>
                          <span>Automatically tag emails based on content</span>
                        </div>
                      </div>
                      <div className="benefit">
                        <div className="benefitIcon">🔍</div>
                        <div className="benefitText">
                          <strong>Easy Organization</strong>
                          <span>Find and filter emails by tags</span>
                        </div>
                      </div>
                      <div className="benefit">
                        <div className="benefitIcon">⚡</div>
                        <div className="benefitText">
                          <strong>Automated Workflow</strong>
                          <span>Trigger actions based on tags</span>
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      className="addTagButton primary-button"
                      onClick={() => setShowAddTag(true)}
                    >
                      🏷️ Create Your First Tag
                    </button>
                    
                    <div className="tagExample">
                      <div className="exampleTitle">Example Tag:</div>
                      <div className="exampleContent">
                        <div className="exampleField">
                          <strong>Name:</strong> Urgent Support Request
                        </div>
                        <div className="exampleField">
                          <strong>Case:</strong> Customer needs immediate assistance or reports critical issues
                        </div>
                        <div className="exampleField">
                          <strong>Tag:</strong> urgent-support
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {tags.length > 0 && (
                    <div className="tagsList">
                      {tags.map((tag) => (
                        <div key={tag.id} className="tagItem" data-tag-id={tag.id}>
                          <div className="tagInfo">
                            <div className="tagHeader">
                              <div className="tagName">{tag.name}</div>
                              <div 
                                className="tagBadge"
                                style={{ backgroundColor: tag.color }}
                              >
                                {tag.icon && (
                                  <img 
                                    src={tag.icon} 
                                    alt="Tag Icon" 
                                    className="tagBadgeIcon"
                                  />
                                )}
                                {tag.tag}
                              </div>
                            </div>
                            <div className="tagCase">{tag.case}</div>
                            {tag.description && (
                              <div className="tagDescription">{tag.description}</div>
                            )}
                          </div>
                          <div className="tagActions">
                            <button 
                              className="editTagButton"
                              onClick={() => handleEditTag(tag)}
                              title="Edit Tag"
                            >
                              <img src="/img/edit.svg" alt="Edit" className="actionIcon" />
                            </button>
                            <button 
                              className="deleteTagButton"
                              onClick={() => handleDeleteTag(tag)}
                              title="Delete Tag"
                            >
                              <img src="/img/delete.svg" alt="Delete" className="actionIcon" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {tagsMessage && (
              <div className="tagsMessage">{tagsMessage}</div>
            )}
            
            {showAddTag && (
              <Modal isOpen={showAddTag} onClose={handleCancelAdd}>
                <div className="tagForm addForm">
                  <div className="tagFormHeader">
                    <div className="formIcon"><img src="/img/bag.svg" alt="AI" style={{ width: '20px', height: '20px', filter: 'invert(1)' }} /></div>
                    <h4>Add New Tag</h4>
                    <button className="aiTagsModernModalCloseButton" onClick={handleCancelAdd}>×</button>
                  </div>
                  
                  <div className="aiTagsModernModalFormBody">
                    <div className="settingField">
                      <label>Name:</label>
                      <input
                        type="text"
                        className="aiTagsModernModalInput"
                        value={newTag.name}
                        onChange={(e) => setNewTag(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter tag name"
                      />
                    </div>
                    
                    <div className="settingField">
                      <label>Case Description:</label>
                      <textarea
                        className="aiTagsModernModalTextarea"
                        value={newTag.case}
                        onChange={(e) => setNewTag(prev => ({ ...prev, case: e.target.value }))}
                        placeholder="Describe when this tag should be applied"
                        rows="3"
                      />
                      <small className="fieldHint">
                        Describe the conditions under which this tag should be applied to an email.
                      </small>
                    </div>
                    
                    <div className="settingField">
                      <label>Tag:</label>
                      <input
                        type="text"
                        className="aiTagsModernModalInput"
                        value={newTag.tag}
                        onChange={(e) => setNewTag(prev => ({ ...prev, tag: e.target.value }))}
                        placeholder="Enter tag value (e.g., 'urgent', 'follow-up')"
                      />
                      <small className="fieldHint">
                        This is the actual tag that will be applied to matching emails.
                      </small>
                    </div>
                    
                    <div className="settingField">
                      <label>Description:</label>
                      <textarea
                        className="aiTagsModernModalTextarea"
                        value={newTag.description}
                        onChange={(e) => setNewTag(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Optional description of this tag's purpose"
                        rows="2"
                      />
                    </div>
                    
                    <div className="settingField">
                      <label>Color:</label>
                      <div className="aiTags-colorSelection">
                        {predefinedColors.map((color, index) => (
                          <button
                            key={index}
                            className={`aiTags-colorOption ${newTag.color === color ? 'aiTags-colorOption--selected' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setNewTag(prev => ({ ...prev, color }))}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="settingField">
                      <label>Icon:</label>
                      <div className="aiTags-iconSelection">
                        <button
                          className={`aiTags-iconOption ${!newTag.icon ? 'aiTags-iconOption--selected' : ''}`}
                          onClick={() => setNewTag(prev => ({ ...prev, icon: '' }))}
                        >
                          None
                        </button>
                        {availableIcons.map((icon) => (
                          <button
                            key={icon.name}
                            className={`aiTags-iconOption ${newTag.icon === icon.path ? 'aiTags-iconOption--selected' : ''}`}
                            onClick={() => setNewTag(prev => ({ ...prev, icon: icon.path }))}
                          >
                            <img src={icon.path} alt={icon.name} className="aiTags-iconPreview" />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="settingField">
                      <label>Topic for Reply:</label>
                      <select
                        className="aiTagsModernModalSelect"
                        value={newTag.selectedTopic}
                        onChange={(e) => setNewTag(prev => ({ ...prev, selectedTopic: e.target.value }))}
                      >
                        <option value="">No automatic reply topic</option>
                        {Object.entries(userTopics)
                          .filter(([_, topic]) => topic.type === 'custom_reply')
                          .map(([id, topic]) => (
                            <option key={id} value={topic.topic}>
                              {topic.topic}
                            </option>
                          ))
                        }
                      </select>
                      <small className="fieldHint">
                        When this tag is applied, this topic will be automatically selected for replies.
                      </small>
                    </div>
                  </div>

                  <div className="aiTagsModernModalFormActions">
                    <div className="tagFormActions">
                      <button 
                        className="cancelTagButton"
                        onClick={handleCancelAdd}
                      >
                        Cancel
                      </button>
                      <button 
                        className="saveTagButton"
                        onClick={handleAddTag}
                      >
                        Add Tag
                      </button>
                    </div>
                  </div>
                </div>
              </Modal>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default AITagsSettings; 